import { useEffect, useState } from 'react'
import { Stack, Typography, Box, useTheme, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FsStepper from '../../components/fs-etl/common/Stepper'
import Fixtures from '../../components/fs-etl/fs-internal/Fixtures'
import ValueMapping from '../../components/fs-etl/fs-internal/ValueMapping'
import ColumnMatching from '../../components/fs-etl/fs-internal/ColumnMatching'
import AdditionalInformation from '../../components/fs-etl/fs-internal/AdditionalInformation'
import CheckingFiles from '../../components/fs-etl/fs-internal/CheckingFiles'
import FinalCheck from '../../components/fs-etl/fs-internal/FinalCheck'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { changeCurrentStep } from '../../store/actions'
import { ReactComponent as NoDataIllu } from '../../assets/etl/InProgress.svg'
import { ReactComponent as UploadSuccessful } from '../../assets/Upload_success.svg'
import MergeFiles from '../../components/fs-etl/fs-internal/MergeFiles'
import { useLoading } from '../../shared/hooks'
import ACTIONS from '../../store/constants'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'

const FsETLInternalView = () => {
  const { currentStep, latestEtlStatus } = useAppSelector((state) => state.currentEtl)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const theme = useTheme()
  const navigate = useNavigate()

  const ETL_INTERNAL_STEPS = [
    t('files.checking'),
    t('column.matching'),
    t('files.process'),
    t('additional.information'),
    t('value.mapping'),
    t('final.check.etl'),
    t('fixtures'),
  ]

  const { isFetching } = useLoading([ACTIONS.ETL.SET_ETL_STATUS])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  useEffect(() => {
    if (latestEtlStatus) {
      if (latestEtlStatus.step <= 2) dispatch(changeCurrentStep(0))
      else if (latestEtlStatus.step === 3) dispatch(changeCurrentStep(1))
      else if (latestEtlStatus.step <= 5) dispatch(changeCurrentStep(2))
      else if (latestEtlStatus.step <= 11) dispatch(changeCurrentStep(latestEtlStatus.step - 3))
    } else {
      dispatch(changeCurrentStep(0))
    }
  }, [latestEtlStatus])

  const getCurrentView = () => {
    switch (currentStep) {
      case 0:
        return (
          <Stack mt={4} alignItems="center" sx={{ maxWidth: 800 }}>
            {latestEtlStatus?.status ? <UploadSuccessful /> : <NoDataIllu />}
            <Typography mt={4} textAlign="center">
              {latestEtlStatus?.status ? t('previous.process.done') : t('no.process')}
            </Typography>
            {latestEtlStatus?.status && (
              <>
                <Typography mt={2} fontWeight="bold">
                  {t('go.to.configuration.wizard')}
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/fs/admin/config')}
                >
                  {t('configuration.wizard')}
                </Button>
              </>
            )}
          </Stack>
        )
      case 1:
        return (
          <Stack mt={4} alignItems="center" sx={{ maxWidth: 800 }}>
            <NoDataIllu />
            <Typography mt={4} textAlign="center">
              {t('data.preparation.not.started')}
            </Typography>
          </Stack>
        )
      case 2:
        return <CheckingFiles setIsLoading={setIsLoading} />
      case 3:
        return <ColumnMatching setIsLoading={setIsLoading} />
      case 4:
        return <MergeFiles setIsLoading={setIsLoading} />
      case 5:
        return <AdditionalInformation setIsLoading={setIsLoading} />
      case 6:
        return <ValueMapping setIsLoading={setIsLoading} />
      case 7:
        return <FinalCheck setIsLoading={setIsLoading} />
      case 8:
        return <Fixtures setIsLoading={setIsLoading} />
      default:
        return null
    }
  }

  return (
    <Stack alignItems="flex-start" ml={2}>
      <Stack direction="row" alignItems="center" mb={2}>
        <Box mt={1}>{getIcon('etl')}</Box>
        <Typography variant="h5" ml={1} pr={2}>
          {t('upload.data')}
        </Typography>
      </Stack>
      {currentStep > 1 && (
        <Stack width="100%" direction="column" gap={2}>
          <FsStepper steps={ETL_INTERNAL_STEPS} activeStep={currentStep - 2} />
          <Typography variant="h5">{ETL_INTERNAL_STEPS[currentStep - 2]}</Typography>
        </Stack>
      )}
      <Box width="100%" height="calc(100vh - 258px)" mt={-1}>
        {getCurrentView()}
      </Box>
      {isLoading && <FsFullPageLoader />}
    </Stack>
  )
}
export default FsETLInternalView
